import { z } from "zod";
import { completionSelectionSchema } from "./completion-picker";
import {
  COMPLETION_ITEM_SOURCE_LABEL,
  completionRoundParentKindSchema,
  completionRoundSchema,
  type CompletionRoundItem,
} from "./completion-round";
import {
  journalFinalVersionSchema,
  journalWebdocStatusSchema,
} from "./journal-provenance";
import {
  labReviewOutcomeSchema,
  labSampleCostSchema,
  refineNewSampleLabOrder,
  type LabReviewOutcome,
} from "./labs";
import { webdocControlSchema } from "./prescription-choice";
import type { JournalDecision } from "./journal-decision";
import type { MeetingPrescriptionOrder } from "./prescription-cycle";
import {
  JOURNAL_SECTION_MAX,
  assembleJournalTextFor,
  type JournalLayout,
} from "./meeting-journal";

// TES-1060 (TES-1054 points C and E, Emelie 2026-10-01).
//
// C: "När kompletteringar senare kommer in skapas en ny, daterad Kompletteringsanteckning i samma CareCase."
// "Den tidigare färdigställda journalanteckningen ska visas som låst historik eller separat referens. Den får inte byggas ihop med den nya texten."
// "Kopieringsknappen ska endast kopiera den aktuella kompletteringsanteckningen."
//
// The note belongs to one completion round (TES-1059): its versions are
// journal_revisions rows with parentKind `completion_note` and the round's
// id, and its "Införd i Webdoc" receipt is a journal_webdoc_receipts row of
// the same kind. The earlier note is never edited by it and never part of
// its text.

export const COMPLETION_NOTE_TITLE = "Kompletteringsanteckning";

// Emelie: Kirill sees "endast de nya underlagen, deras källa och datum" and "den nya medicinska bedömningen, utfallet, ordinationen och planen".
// Point E adds the patient feedback he writes with the lab assessment.
//
// TES-1400 (Ö-455, Emelie 2026-10-15) — "Den separata journalrubriken
// Ordination tas bort även i receptövertag. Strukturerat recept förs
// automatiskt in under Åtgärd och behandlingsplan." The note's "Ordination"
// and "Plan" became the care meeting's "Åtgärd och behandlingsplan"
// (TES-1328): the order, the decision's plan lines and the monthly follow-up
// are written there when the note is compiled, followed by what the doctor
// writes in the box.
export const COMPLETION_NOTE_SECTION_KEYS = [
  "nya_underlag",
  "bedomning",
  "utfall",
  "atgard_behandlingsplan",
  "aterkoppling",
] as const;
export const completionNoteSectionKeySchema = z.enum(
  COMPLETION_NOTE_SECTION_KEYS,
);
export type CompletionNoteSectionKey = z.infer<
  typeof completionNoteSectionKeySchema
>;

export const COMPLETION_NOTE_HEADINGS: Record<
  CompletionNoteSectionKey,
  string
> = {
  nya_underlag: "Nya underlag",
  bedomning: "Medicinsk bedömning",
  utfall: "Utfall",
  atgard_behandlingsplan: "Åtgärd och behandlingsplan",
  aterkoppling: "Återkoppling till patienten",
};

// The two boxes "Åtgärd och behandlingsplan" replaced. A note saved before
// TES-1400 keeps their text: it is read into the new box (in this order)
// until the doctor saves that box himself.
export const COMPLETION_NOTE_LEGACY_PLAN_KEYS = ["ordination", "plan"] as const;

/** The text of the replaced boxes, as the new box starts with it. */
export function completionNoteLegacyPlanText(
  edits: ReadonlyMap<string, string>,
): string {
  return COMPLETION_NOTE_LEGACY_PLAN_KEYS.map((k) =>
    (edits.get(k) ?? "").trim(),
  )
    .filter(Boolean)
    .join("\n");
}

// Empty headings are left out of the copied text, like every other note.
// TES-1061 — the treatment decision taken on the completed evidence is
// journaled here (TES-1069 point 1: "Dokumentera kompletteringen och behandlingsbeslutet"):
// its outcome and motivation lead "Utfall"; its requested kompletteringar,
// prescription status and care path lead "Åtgärd och behandlingsplan"
// (journalActionPlanText), after the order when one is given.
export const COMPLETION_NOTE_LAYOUT: JournalLayout<CompletionNoteSectionKey> = {
  keys: COMPLETION_NOTE_SECTION_KEYS,
  headings: COMPLETION_NOTE_HEADINGS,
  assessmentSection: "utfall",
  generatedSection: "atgard_behandlingsplan",
};

/** The note's date as printed in its title: the Stockholm calendar day. */
export function completionNoteDate(iso: string): string {
  return new Intl.DateTimeFormat("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: "Europe/Stockholm",
  }).format(new Date(iso));
}

/**
 * The text the copy button copies: the dated title and this note's own
 * boxes. Nothing from the earlier note is ever part of it. `order` is the
 * structured order being decided (the transfer's approval), written under
 * "Åtgärd och behandlingsplan" (Ö-455).
 */
export function assembleCompletionNoteText(
  noteDate: string,
  sections: readonly { key: CompletionNoteSectionKey; content: string }[],
  decision?: JournalDecision | null,
  order?: MeetingPrescriptionOrder | null,
): string {
  const body = assembleJournalTextFor(COMPLETION_NOTE_LAYOUT, sections, {
    decision: decision ?? null,
    order: order ?? null,
  });
  return body ? `${COMPLETION_NOTE_TITLE} ${noteDate}\n\n${body}` : "";
}

// A round item is evidence for the note when it was supplied. Ö-136 and
// Emelie's point E: "Jag har lämnat blodprovet" is "en operativ självrapport" and "ska inte journalföras som återkoppling om provresultatet".
// So a new blood test the patient only reported as given is not evidence,
// and nor is an open or waived item.
//
// TES-1095 (Emelie, TES-1093 rule 8) — nor is a payment. A verified
// blood-test payment is commercial, not medical, and the rule is that no
// payment status reaches the journal:
// "Abonnemangsval, betalningsstatus, betalningslänk och annan kommersiell eller administrativ abonnemangsinformation ska inte ingå i journalanteckningen."
export function isCompletionNoteEvidence(
  item: Pick<CompletionRoundItem, "key" | "status" | "source">,
): boolean {
  if (item.status !== "done") return false;
  if (item.source === "payment") return false;
  return !(item.key === "new_blood_test" && item.source === "self_report");
}

/**
 * The prefill of "Nya underlag": one line per supplied item, with its
 * source and date. Facts only; the doctor's assessment goes under its own
 * heading.
 */
export function completionNoteEvidenceText(
  items: readonly Pick<
    CompletionRoundItem,
    "key" | "label" | "status" | "source" | "doneAt"
  >[],
): string {
  return items
    .filter(isCompletionNoteEvidence)
    .map((i) => {
      const meta = [
        i.source ? COMPLETION_ITEM_SOURCE_LABEL[i.source] : null,
        i.doneAt ? completionNoteDate(i.doneAt) : null,
      ].filter(Boolean);
      return meta.length > 0 ? `${i.label}: ${meta.join(", ")}` : i.label;
    })
    .join("\n");
}

export const completionNoteSectionSchema = z.object({
  key: completionNoteSectionKeySchema,
  heading: z.string(),
  content: z.string(),
});
export type CompletionNoteSection = z.infer<typeof completionNoteSectionSchema>;

// The earlier, finished note, shown beside the workspace and never edited
// from it. `locked`: it has a Webdoc receipt and this round's note exists, so
// the API refuses edits to it.
export const completionNoteEarlierNoteSchema = z.object({
  parentKind: z.enum(["meeting", "cycle", "transfer_review"]),
  parentId: z.string(),
  text: z.string(),
  webdoc: journalWebdocStatusSchema,
  locked: z.boolean(),
});
export type CompletionNoteEarlierNote = z.infer<
  typeof completionNoteEarlierNoteSchema
>;

// ---- Point E: "Hantera provsvar från Webdoc" --------------------------------
//
// Emelie: "När provsvaret finns får Kirill notisen i Webdoc. I Ella öppnar han patientens aktuella provärende och väljer: Hantera provsvar från Webdoc"
// "registrera att provunderlaget har bedömts, välja Underlaget är godkänt, Ytterligare komplettering krävs eller Nytt prov krävs, skriva eller bekräfta sin medicinska bedömning och patientåterkoppling, spara bedömningen i utkastet till kompletteringsanteckningen."
// "Om andra kompletteringar återstår sparas provbedömningen tills hela paketet är klart."

export const LAB_WEBDOC_ACTION_LABEL = "Hantera provsvar från Webdoc";

// Her three choices, onto LabOrder.reviewOutcome.
export const LAB_WEBDOC_OUTCOME_LABEL: Record<LabReviewOutcome, string> = {
  approved: "Underlaget är godkänt",
  completion_required: "Ytterligare komplettering krävs",
  new_sample_required: "Nytt prov krävs",
};

// The line the assessment leads "Utfall" with. Replaced, not appended, when
// he records the assessment again.
export const LAB_WEBDOC_OUTCOME_LINE_PREFIX = "Provsvar från Webdoc: ";
export function labWebdocOutcomeLine(outcome: LabReviewOutcome): string {
  return `${LAB_WEBDOC_OUTCOME_LINE_PREFIX}${LAB_WEBDOC_OUTCOME_LABEL[outcome]}`;
}

/** "Utfall" with this assessment's line in place of an earlier one. */
export function withLabWebdocOutcomeLine(
  current: string,
  outcome: LabReviewOutcome,
): string {
  const kept = current
    .split("\n")
    .filter((l) => !l.startsWith(LAB_WEBDOC_OUTCOME_LINE_PREFIX));
  while (kept.length > 0 && !kept[0]!.trim()) kept.shift();
  return [labWebdocOutcomeLine(outcome), ...kept].join("\n").trim();
}

// The doctor's lab assessment. The review fields are the lab review's
// (reviewLabOrderBodySchema): the server applies them through the same
// path. A komplettering or a new sample always names what is asked for.
export const labWebdocAssessmentSchema = z
  .object({
    // "registrera att provunderlaget har bedömts" — an explicit tick.
    assessed: z.literal(true),
    outcome: labReviewOutcomeSchema,
    sampleCost: labSampleCostSchema.optional(),
    completion: completionSelectionSchema.optional(),
    assessment: z.string().max(JOURNAL_SECTION_MAX).default(""),
    patientFeedback: z.string().max(JOURNAL_SECTION_MAX).default(""),
    // TES-1181 — the remiss tick, required on "Nytt prov krävs".
    webdocControl: webdocControlSchema.optional(),
  })
  .superRefine((v, ctx) => {
    refineNewSampleLabOrder(v, ctx);
    if (v.outcome === "approved") {
      if (v.completion) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["completion"],
          message: "completion_not_applicable",
        });
      }
      if (v.sampleCost) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sampleCost"],
          message: "sample_cost_not_applicable",
        });
      }
      return;
    }
    if (!v.completion) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["completion"],
        message: "completion_required",
      });
      return;
    }
    const newSample = v.completion.items.includes("new_blood_test");
    if (v.outcome === "new_sample_required" && !newSample) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["completion"],
        message: "new_sample_requires_new_blood_test",
      });
    }
  });
export type LabWebdocAssessment = z.infer<typeof labWebdocAssessmentSchema>;

// What is stored while the assessment is held: the review, nothing else.
// The texts are in the note.
export const heldLabReviewSchema = z.object({
  outcome: labReviewOutcomeSchema,
  sampleCost: labSampleCostSchema.optional(),
  completion: completionSelectionSchema.optional(),
});
export type HeldLabReview = z.infer<typeof heldLabReviewSchema>;

export const labWebdocAssessmentStateSchema = z.object({
  orderId: z.string(),
  assessedAt: z.string().nullable(),
  // The held review while other items remain; null once applied.
  held: heldLabReviewSchema.nullable(),
  // The stamped outcome, once applied.
  reviewOutcome: labReviewOutcomeSchema.nullable(),
});
export type LabWebdocAssessmentState = z.infer<
  typeof labWebdocAssessmentStateSchema
>;

// ---- TES-1347 (Ö-295): "Läkarens besked om provsvar" --------------------
//
// CP-06 / 09C: "Första versionen har endast dessa patientriktade fritextkällor: A. Sammanfattning efter vårdmötet. B. Läkarens kommentar till behandlingsbeslutet. C. Läkarens besked om provsvar."
// "Efter provsvar väljer läkaren ett av tre utfall: 1. Provsvar granskat – behandlingsplanen ändras inte. 2. Ytterligare utredning eller komplettering krävs. 3. Fatta behandlingsbeslut utifrån samlat underlag."
// "Utfall 3 publicerar ett sammanhållet besked … Patienten ska inte få dubbla meddelanden om provsvaret och beslutet."
//
// His patient feedback in "Hantera provsvar från Webdoc" is published as a
// `doctor_notice` whose subject is the LabOrder, so every build that lists
// notices reads it, with the notice push and the Home card. One per lab
// order: a later correction would be its version 2. What happened to it is
// the assessment's `feedbackNotice`:
//   published         — published now (outcome 1 or 2).
//   in_decision       — a treatment decision follows on this evidence
//                       (outcome 3): she reads it in that decision's notice,
//                       so nothing is published here.
//   held              — other kompletteringar remain; decided again when he
//                       confirms the assessment.
//   already_published — the order's word already reached her through the
//                       lab results view (TES-1370 "Publicera").
//   empty             — he wrote no feedback.
//   disabled          — the box has the notice switched off
//                       (LAB_FEEDBACK_NOTICE_ENABLED); it stays in the note.
// In every case the text is saved in the Kompletteringsanteckning.
export const LAB_FEEDBACK_NOTICE_SUBJECT_TYPE = "LabOrder";
export const LAB_FEEDBACK_NOTICE_OUTCOMES = [
  "published",
  "in_decision",
  "held",
  "already_published",
  "empty",
  "disabled",
] as const;
export const labFeedbackNoticeOutcomeSchema = z.enum(
  LAB_FEEDBACK_NOTICE_OUTCOMES,
);
export type LabFeedbackNoticeOutcome = z.infer<
  typeof labFeedbackNoticeOutcomeSchema
>;

export const labWebdocAssessmentResultSchema = z.object({
  held: z.boolean(),
  roundId: z.string(),
  reviewOutcome: labReviewOutcomeSchema.nullable(),
  // TES-1347 — optional so the admin reads an older API.
  feedbackNotice: labFeedbackNoticeOutcomeSchema.optional(),
});
export type LabWebdocAssessmentResult = z.infer<
  typeof labWebdocAssessmentResultSchema
>;

// ---- The workspace -----------------------------------------------------------

export const completionNoteSchema = z.object({
  roundId: z.string(),
  round: completionRoundSchema,
  parentKind: completionRoundParentKindSchema,
  // The note's date (its first version, or today before there is one).
  noteDate: z.string(),
  sections: z.array(completionNoteSectionSchema),
  earlierNote: completionNoteEarlierNoteSchema.nullable(),
  webdoc: journalWebdocStatusSchema,
  // The lab assessment of the case's blood test, when there is one.
  labAssessment: labWebdocAssessmentStateSchema.nullable(),
});
export type CompletionNote = z.infer<typeof completionNoteSchema>;

// PUT …/completion-rounds/:roundId/note — the boxes he changed.
export const updateCompletionNoteSchema = z.object({
  sections: z
    .array(
      z.object({
        key: completionNoteSectionKeySchema,
        content: z.string().max(JOURNAL_SECTION_MAX),
      }),
    )
    .min(1)
    .max(COMPLETION_NOTE_SECTION_KEYS.length),
});
export type UpdateCompletionNote = z.infer<typeof updateCompletionNoteSchema>;

// POST …/note/finalize — the exact text stored as the final version.
export const completionNoteFinalizedSchema = journalFinalVersionSchema.extend({
  version: z.number().int().positive(),
  text: z.string(),
  webdoc: journalWebdocStatusSchema,
});
export type CompletionNoteFinalized = z.infer<
  typeof completionNoteFinalizedSchema
>;
